import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, Save, AlertTriangle, Download, Upload } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ChecklistItem {
  id: string;
  label: string;
  status: 'complete' | 'in_progress' | 'pending';
  details?: string;
}

type StageStatus = 'not_started' | 'in_progress' | 'completed';

interface StageNavigationBarProps {
  status: StageStatus;
  progress: number;
  checklist: ChecklistItem[];
  canProceed: boolean;
  warningMessage?: string;
  onBack: () => void;
  onSave: () => void;
  onNext: () => void;
  onExport?: () => void;
  onReimport?: () => void;
  isSaving?: boolean;
  hasData?: boolean;
}

/**
 * StageNavigationBar - Compact sticky bottom navigation
 */
export default function StageNavigationBar({
  status,
  progress,
  checklist,
  canProceed,
  warningMessage,
  onBack,
  onSave,
  onNext,
  onExport,
  onReimport,
  isSaving = false,
  hasData = false,
}: StageNavigationBarProps) {
  const completedCount = checklist.filter(c => c.status === 'complete').length;

  const getStatusBadge = () => {
    switch (status) {
      case 'completed':
        return <Badge className="bg-emerald-500 text-xs h-5 px-1.5">Complete</Badge>;
      case 'in_progress':
        return <Badge variant="secondary" className="text-xs h-5 px-1.5">In Progress</Badge>;
      default:
        return <Badge variant="outline" className="text-xs h-5 px-1.5">Not Started</Badge>;
    }
  };

  return (
    <div className="sticky bottom-0 border-t border-border bg-background px-3 py-2 shadow-lg">
      <div className="flex items-center justify-between gap-4">
        {/* Left: Status & Progress */}
        <div className="flex items-center gap-3">
          {getStatusBadge()}
          <div className="flex items-center gap-2">
            <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden">
              <div 
                className={cn(
                  "h-full transition-all",
                  progress >= 100 ? "bg-emerald-500" : "bg-primary"
                )}
                style={{ width: `${Math.min(progress, 100)}%` }} 
              />
            </div>
            <span className="text-xs text-muted-foreground">{progress}%</span>
          </div>
          <span className="text-xs text-muted-foreground hidden sm:inline">
            {completedCount}/{checklist.length} steps
          </span>
        </div>

        {/* Center: Warning if not can proceed */}
        {!canProceed && (
          <div className="flex items-center gap-1 text-xs text-amber-600">
            <AlertTriangle className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Fix errors to proceed</span>
          </div>
        )}

        {/* Right: Actions */}
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={onBack} className="h-7 text-xs gap-1">
            <ArrowLeft className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Back</span>
          </Button>
          
          {/* Export/Import buttons */}
          {hasData && onExport && (
            <Button variant="outline" size="sm" onClick={onExport} className="h-7 text-xs gap-1">
              <Download className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Export</span>
            </Button>
          )}
          {hasData && onReimport && (
            <Button 
              variant="outline" 
              size="sm" 
              onClick={onReimport} 
              className="h-7 text-xs gap-1"
              title="Upload a file to replace or update data. Can be a fresh CSV or an exported file with edits."
            >
              <Upload className="h-3.5 w-3.5" />
              <span className="hidden sm:inline">Upload / Replace</span>
            </Button>
          )}
          
          <Button variant="outline" size="sm" onClick={onSave} disabled={isSaving} className="h-7 text-xs gap-1">
            <Save className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">{isSaving ? 'Saving...' : 'Save'}</span>
          </Button>
          <Button 
            size="sm" 
            onClick={onNext} 
            disabled={!canProceed}
            className="h-7 text-xs gap-1"
          >
            <span className="hidden sm:inline">Next</span>
            <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}
